import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  clearLabsEnabled,
  isLabsUnlocked,
  persistLabsEnabled,
} from '@vaiu/utils';
import { 
  User, 
  LogOut,
  Settings,
  HelpCircle,
  ShoppingBag,
  Wallet,
  LayoutDashboard,
  Store,
  Home,
  HeadphonesIcon,
  Users,
  MessageSquare,
  Terminal,
  ArrowRightLeft,
  Code,
  History,
  Gift,
  Receipt,
  TrendingUp,
  BarChart3,
  Lock,
  Beaker,
  Wrench,
  Briefcase,
  X,
  CreditCard,
  Inbox,
} from 'lucide-react';
import { Button } from '@vaiu/ui/button';
import { Badge } from '@vaiu/ui/badge';
import { Card, CardContent } from '@vaiu/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuLabel,
  DropdownMenuGroup,
} from "@vaiu/ui/dropdown-menu";
import { GuestLogoutConfirmBar } from '@vaiu/ui';
import { useAuth } from '@/context/AuthContext';
import { useConfig } from '@/context/ConfigContext';
import { hasAdminAccess, isCustomerSupport } from '@/utils/rolePermissions';
import { i18n } from '@/utils/i18n';
import Footer from '@/components/shared/Footer';
import TenantBrandBar, { PoweredByVaiuMark } from '@/components/shared/TenantBrandBar';
import BrandLogo from '@/components/shared/BrandLogo';
import { resolveTenantBranding } from '@/utils/tenantBranding';
import BottomNavBar from '@/components/shared/BottomNavBar';
import VersionInfo from '@/components/shared/VersionInfo';
import GuestSessionIndicator from './GuestSessionIndicator';
import GuestUpgradePrompt from './GuestUpgradePrompt';
import CompactLanguageSelector from './CompactLanguageSelector';

interface AppShellProps {
  children: React.ReactNode;
  showHeader?: boolean;
  showFooter?: boolean;
}

interface ShellNavItem {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  to?: string;
  onClick?: () => void;
  show: boolean;
  active: boolean;
}

const AppShell: React.FC<AppShellProps> = ({
  children, 
  showHeader = true, 
  showFooter = true 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isGuest } = useAuth();
  const { config } = useConfig();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showGuestLogoutConfirm, setShowGuestLogoutConfirm] = useState(false);

  const isAdmin = hasAdminAccess(user?.roles || []);
  const isSupport = isCustomerSupport(user?.roles || []);
  const isMerchant = user?.roles?.includes('MERCHANT');
  const isDeveloper = user?.roles?.includes('DEVELOPER');
  
  const searchParams = new URLSearchParams(location.search);
  const labsFlag = searchParams.get('labs');
  if (labsFlag === 'on') persistLabsEnabled();
  if (labsFlag === 'off') clearLabsEnabled();
  const isLabsEnabled = isLabsUnlocked(location.search);
  const isIntellectEnabled = searchParams.get('intellect') === 'on';
  /** Admin pages use AdminLayout with a fixed sidebar; max-w-7xl centering breaks pl-64 offset and overlaps the sidebar */
  const isAdminRoute = location.pathname.startsWith('/admin');
  const showShellNav = config?.features?.showVaiuShell !== false;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const flag = params.get('labs');
    if (flag !== 'on' && flag !== 'off') return;
    params.delete('labs');
    const search = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: search ? `?${search}` : '',
        hash: location.hash,
      },
      { replace: true }
    );
  }, [location.hash, location.pathname, location.search, navigate]);

  const handleLogout = async () => {
    // Show top-of-app confirmation for guest users
    if (isGuest) {
      // Defer until after dropdown closes so the menu doesn’t steal focus from the new UI.
      window.setTimeout(() => setShowGuestLogoutConfirm(true), 0);
      return;
    }
    
    // Regular users can logout directly
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleConfirmGuestLogout = async () => {
    setShowGuestLogoutConfirm(false);
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };


  // Primary navigation (Pay stays in header / bottom bar; rest in the account menu)
  const primaryNavItems: ShellNavItem[] = [
    {
      label: 'Dashboard',
      icon: Home,
      to: '/dashboard',
      show: true,
      active: location.pathname === '/dashboard' || location.pathname === '/',
    },
    {
      label: 'Marketplace',
      icon: Store,
      to: '/marketplace',
      show: true,
      active: location.pathname === '/marketplace' || location.pathname.startsWith('/marketplace/'),
    },
    {
      label: 'Pay',
      icon: CreditCard,
      to: '/pay',
      show: true,
      active: location.pathname === '/pay',
    },
    {
      label: 'Messages',
      icon: MessageSquare,
      to: '/messages',
      show: true,
      active: location.pathname === '/messages' || location.pathname === '/conversations',
    },
    {
      label: 'Contacts',
      icon: Users,
      to: '/contacts',
      show: true,
      active: location.pathname === '/contacts',
    },
  ];

  // Labs: unlock once with ?labs=on (this tab). Menu paths stay clean.
  const labsItems: ShellNavItem[] = [
    {
      label: 'Portfolio',
      icon: Briefcase,
      to: '/portfolio',
      show: isLabsEnabled && config?.features?.showPortfolio !== false,
      active: location.pathname === '/portfolio',
    },
    {
      label: 'Currency Exchange',
      icon: ArrowRightLeft,
      to: '/exchange',
      show: isLabsEnabled && config?.features?.showExchange !== false,
      active: location.pathname === '/exchange',
    },
    {
      label: 'Stock Exchange',
      icon: BarChart3,
      to: '/stocks',
      show: isLabsEnabled && config?.features?.showStockExchange !== false,
      active: location.pathname === '/stocks' || location.pathname === '/stock-exchange',
    },
    {
      label: 'Smart Transactions',
      icon: Lock,
      to: '/smart-transactions',
      show: isLabsEnabled && config?.features?.showSmartTransactions !== false,
      active: location.pathname === '/smart-transactions',
    },
  ];

  // Intellect Design features - only show if ?intellect=on is in URL
  const intellectItems = [
    {
      label: 'Intellect Design Test',
      icon: Code,
      onClick: () => navigate('/intellect-test?intellect=on'),
      show: isIntellectEnabled,
      active: location.pathname === '/intellect-test'
    }
  ];

  const payNavItem = primaryNavItems.find((item) => item.label === 'Pay');
  /** All primary links except Pay — account menu (desktop) / bottom drawer (mobile). */
  const menuNavItems = primaryNavItems.filter(
    (item) => item.label !== 'Pay' && item.show
  );

  const renderDropdownNavItem = (item: ShellNavItem) => {
    const Icon = item.icon;
    const itemClassName = item.active ? 'bg-accent' : undefined;

    if (item.to) {
      return (
        <DropdownMenuItem key={item.label} asChild className={itemClassName}>
          <Link to={item.to} className="flex w-full cursor-pointer items-center">
            <Icon className="mr-2 h-4 w-4" />
            <span>{item.label}</span>
          </Link>
        </DropdownMenuItem>
      );
    }

    return (
      <DropdownMenuItem
        key={item.label}
        className={itemClassName}
        onClick={() => item.onClick?.()}
      >
        <Icon className="mr-2 h-4 w-4" />
        <span>{item.label}</span>
      </DropdownMenuItem>
    );
  };

  const showAccountToolsInMenu =
    showShellNav || isDeveloper || isAdmin || (isSupport && !isAdmin);

  const tenant = resolveTenantBranding();
  const desktopMenuNavItems: ShellNavItem[] = [
    ...menuNavItems,
    ...(isMerchant
      ? [
          {
            label: 'Merchant Tools',
            icon: Wrench,
            onClick: () => {
              window.location.href = 'https://sell.vaiu.app/merchant-tools';
            },
            show: true,
            active: location.pathname === '/merchant-tools',
          },
        ]
      : []),
    ...intellectItems.filter((item) => item.show),
  ];
  const visibleLabsItems = labsItems.filter((item) => item.show);

  return (
    <div className="min-h-screen page-background overflow-x-hidden">
      <GuestLogoutConfirmBar
        open={showGuestLogoutConfirm}
        onCancel={() => setShowGuestLogoutConfirm(false)}
        onConfirm={handleConfirmGuestLogout}
        title={i18n.__('guest.logout.warning.title') || 'Sign Out as Guest?'}
        description={
          i18n.__('guest.logout.warning.description') ||
          "If you sign out, you will lose access to your guest account. Any cards you've added and your transaction history will become inaccessible unless you sign up for a permanent account."
        }
        cancelLabel={i18n.__('guest.logout.cancel') || 'Cancel'}
        confirmLabel={i18n.__('guest.logout.confirm') || 'Sign Out Anyway'}
      />

      {/* Guest indicators */}
      {isGuest && (
        <div className="px-4 pt-2 sm:pt-4">
          <div className="max-w-4xl mx-auto space-y-2">
            {/* <GuestSessionIndicator /> */}
            <GuestUpgradePrompt variant="banner" showSessionExpiry={false} />
          </div>
        </div>
      )}
      
      {/* Tenant brand strip — mobile only; logo moves into top header from sm+ */}
      {showHeader && <TenantBrandBar className="sticky top-0 z-40 sm:hidden" />}

      {/* Header - Hidden on mobile since we have bottom bar */}
      {showHeader && (
        <div className="hidden sm:block sticky top-0 z-40 bg-white border-b shadow-sm">
          <div className={`h-1 w-full ${tenant.accentBarClass}`} aria-hidden="true" />
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between h-12 md:h-16 gap-4">
              {/* Left side - Logo, account menu, Pay */}
              <div className="flex items-center gap-3 md:gap-5 min-w-0">
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className="flex-shrink-0 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label="Go to dashboard"
                >
                  <BrandLogo size="sm" variant="default" className="max-h-8 md:max-h-9" />
                </button>

                {/* Account menu — single dropdown for nav + account actions (desktop) */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="flex items-center space-x-2">
                      <User className="w-4 h-4" />
                      <span>
                        {user?.username || user?.email || 'Account'}
                      </span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="z-[200] w-56">
                    <div className="px-2 py-1.5">
                      <p className="text-sm font-medium">{user?.username}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                      {isGuest && (
                        <p className="text-xs text-amber-600 font-semibold mt-1">Guest Session</p>
                      )}
                    </div>
                    <DropdownMenuSeparator />

                    {isGuest && (
                      <>
                        <DropdownMenuItem onClick={() => navigate('/login')}>
                          <LogOut className="w-4 h-4 mr-2" />
                          Sign In with Account
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}

                    {desktopMenuNavItems.length > 0 ? (
                      <DropdownMenuGroup>
                        <DropdownMenuLabel>Navigate</DropdownMenuLabel>
                        {desktopMenuNavItems.map((item) => renderDropdownNavItem(item))}
                      </DropdownMenuGroup>
                    ) : null}

                    {visibleLabsItems.length > 0 ? (
                      <>
                        {desktopMenuNavItems.length > 0 ? <DropdownMenuSeparator /> : null}
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger>
                            <Beaker className="w-4 h-4 mr-2" />
                            Labs
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent className="z-[200] w-56">
                            {visibleLabsItems.map((item) => renderDropdownNavItem(item))}
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                      </>
                    ) : null}

                    <DropdownMenuSeparator />

                    <DropdownMenuItem onClick={() => navigate('/inbox')}>
                      <Inbox className="w-4 h-4 mr-2" />
                      Action Inbox
                    </DropdownMenuItem>

                    <DropdownMenuItem onClick={() => navigate('/profile')}>
                      <Settings className="w-4 h-4 mr-2" />
                      Profile Settings
                    </DropdownMenuItem>

                    <DropdownMenuItem onClick={() => navigate('/payment-sources')}>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Funding Sources
                    </DropdownMenuItem>

                    {showAccountToolsInMenu && <DropdownMenuSeparator />}

                    {showShellNav && (
                      <DropdownMenuItem onClick={() => navigate('/shell')}>
                        <Terminal className="w-4 h-4 mr-2" />
                        Vaiu Shell
                      </DropdownMenuItem>
                    )}

                    {isDeveloper && (
                      <DropdownMenuItem onClick={() => window.open('https://developers.vaiu.io', '_blank')}>
                        <Code className="w-4 h-4 mr-2" />
                        Developer Console
                      </DropdownMenuItem>
                    )}

                    {isAdmin && (
                      <DropdownMenuItem onClick={() => navigate('/admin')}>
                        <Settings className="w-4 h-4 mr-2" />
                        Admin Panel
                      </DropdownMenuItem>
                    )}

                    {isSupport && !isAdmin && (
                      <DropdownMenuItem onClick={() => navigate('/admin/support')}>
                        <HeadphonesIcon className="w-4 h-4 mr-2" />
                        Support Panel
                      </DropdownMenuItem>
                    )}

                    <DropdownMenuSeparator />

                    <DropdownMenuItem onClick={() => navigate('/support')}>
                      <HeadphonesIcon className="w-4 h-4 mr-2" />
                      Support
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />

                    <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Pay — quick access outside the menu */}
                {payNavItem?.show && (
                  <Button
                    variant={payNavItem.active ? 'secondary' : 'ghost'}
                    size="sm"
                    asChild
                    className="flex items-center space-x-1"
                  >
                    <Link to={payNavItem.to!}>
                      <payNavItem.icon className="w-4 h-4" />
                      <span>{payNavItem.label}</span>
                    </Link>
                  </Button>
                )}

              </div>
              
              {/* Right side - utilities */}
              <div className="flex items-center space-x-3 flex-shrink-0">
                {tenant.showPoweredByVaiu ? <PoweredByVaiuMark compact /> : null}

                {(isAdmin || isSupport) && (
                  <Badge variant="secondary" className="hidden sm:inline-flex">
                    {isAdmin ? 'Admin' : 'Support'}
                  </Badge>
                )}

                <CompactLanguageSelector />
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Main Content — full width for admin so AdminLayout fixed sidebar + md:pl-64 align to viewport */}
      <main className="flex-1 pb-16 md:pb-0 min-w-0 overflow-x-hidden">
        {isAdminRoute ? (
          <div className="w-full min-w-0">{children}</div>
        ) : (
          <div className="max-w-7xl mx-auto min-w-0 px-2 sm:px-4">
            {children}
          </div>
        )}
      </main>
      
      {/* Footer - only on desktop */}
      {showFooter && <Footer className="hidden md:block" />}
      
      {/* Bottom Navigation Bar - only on mobile */}
      <BottomNavBar 
        onMenuClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="md:hidden"
      />
      
      {/* Mobile Menu Drawer */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        >
          <div 
            className="fixed bottom-16 left-0 right-0 bg-white rounded-t-xl shadow-xl max-h-[70vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Menu</h2>
                <div className="flex items-center space-x-2">
                  <CompactLanguageSelector />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {/* Merchant Tools at top if user is merchant */}
              {isMerchant && (
                <>
                  <Button
                    variant="ghost"
                    className="w-full justify-start mb-2"
                    onClick={() => {
                      navigate('/merchant-tools');
                      setMobileMenuOpen(false);
                    }}
                  >
                    <Wrench className="w-4 h-4 mr-2" />
                    Merchant Tools
                  </Button>
                  <div className="border-b mb-2" />
                </>
              )}
              
              {/* Primary navigation (Pay is on the bottom bar) */}
              {menuNavItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Button
                    key={item.label}
                    variant={item.active ? 'secondary' : 'ghost'}
                    className="w-full justify-start mb-2"
                    onClick={() => {
                      if (item.to) {
                        navigate(item.to);
                      } else {
                        item.onClick?.();
                      }
                      setMobileMenuOpen(false);
                    }}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {item.label}
                  </Button>
                );
              })}
              
              {/* Inbox */}
              <Button
                variant={location.pathname === '/inbox' ? "secondary" : "ghost"}
                className="w-full justify-start mb-2"
                onClick={() => {
                  navigate('/inbox');
                  setMobileMenuOpen(false);
                }}
              >
                <Inbox className="w-4 h-4 mr-2" />
                Action Inbox
              </Button>

              {showShellNav && (
                <Button
                  variant={location.pathname === '/shell' ? "secondary" : "ghost"}
                  className="w-full justify-start mb-2"
                  onClick={() => {
                    navigate('/shell');
                    setMobileMenuOpen(false);
                  }}
                >
                  <Terminal className="w-4 h-4 mr-2" />
                  Vaiu Shell
                </Button>
              )}
              
              {/* Labs Section */}
              {labsItems.some(item => item.show) && (
                <>
                  <div className="border-b my-2" />
                  <div className="mb-2">
                    <p className="text-sm font-semibold text-gray-600 mb-2 flex items-center">
                      <Beaker className="w-4 h-4 mr-1" />
                      Labs
                    </p>
                    {labsItems.filter(item => item.show).map(item => (
                      <Button
                        key={item.label}
                        variant={item.active ? "secondary" : "ghost"}
                        className="w-full justify-start mb-1 pl-6"
                        onClick={() => {
                          if (item.to) navigate(item.to);
                          else item.onClick?.();
                          setMobileMenuOpen(false);
                        }}
                      >
                        <item.icon className="w-4 h-4 mr-2" />
                        {item.label}
                      </Button>
                    ))}
                  </div>
                </>
              )}
              
              {/* Intellect Design Section */}
              {intellectItems.some(item => item.show) && (
                <>
                  <div className="border-b my-2" />
                  <div className="mb-2">
                    <p className="text-sm font-semibold text-gray-600 mb-2 flex items-center">
                      <Code className="w-4 h-4 mr-1" />
                      Intellect Design
                    </p>
                    {intellectItems.filter(item => item.show).map(item => (
                      <Button
                        key={item.label}
                        variant={item.active ? "secondary" : "ghost"}
                        className="w-full justify-start mb-1 pl-6"
                        onClick={() => {
                          item.onClick();
                          setMobileMenuOpen(false);
                        }}
                      >
                        <item.icon className="w-4 h-4 mr-2" />
                        {item.label}
                      </Button>
                    ))}
                  </div>
                </>
              )}
              
              {/* Developer Console if developer - Links to external developers.vaiu.io */}
              {isDeveloper && (
                <>
                  <div className="border-b my-2" />
                  <Button
                    variant="ghost"
                    className="w-full justify-start mb-2"
                    onClick={() => {
                      window.open('https://developers.vaiu.io', '_blank');
                      setMobileMenuOpen(false);
                    }}
                  >
                    <Code className="w-4 h-4 mr-2" />
                    Developer Console
                  </Button>
                </>
              )}
              
              {/* Settings and Support */}
              <div className="border-b my-2" />
              <Button
                variant="ghost"
                className="w-full justify-start mb-2"
                onClick={() => {
                  navigate('/profile');
                  setMobileMenuOpen(false);
                }}
              >
                <User className="w-4 h-4 mr-2" />
                Profile
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start mb-2"
                onClick={() => {
                  navigate('/payment-sources');
                  setMobileMenuOpen(false);
                }}
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Funding Sources
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start mb-2"
                onClick={() => {
                  navigate('/settings');
                  setMobileMenuOpen(false);
                }}
              >
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start mb-2"
                onClick={() => {
                  navigate('/support');
                  setMobileMenuOpen(false);
                }}
              >
                <HeadphonesIcon className="w-4 h-4 mr-2" />
                Support
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start mb-2 text-red-600 hover:text-red-700"
                onClick={() => {
                  setMobileMenuOpen(false);
                  void handleLogout();
                }}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
              
              {/* Footer Links - Legal & Company Info */}
              <div className="border-b my-2" />
              <div className="px-2 py-2">
                <p className="text-xs font-semibold text-gray-600 mb-2">Legal & Company</p>
                <div className="grid grid-cols-2 gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="justify-start h-8 px-2 text-xs"
                    onClick={() => {
                      navigate('/terms-of-service');
                      setMobileMenuOpen(false);
                    }}
                  >
                    Terms
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="justify-start h-8 px-2 text-xs"
                    onClick={() => {
                      navigate('/privacy-policy');
                      setMobileMenuOpen(false);
                    }}
                  >
                    Privacy
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="justify-start h-8 px-2 text-xs"
                    onClick={() => {
                      navigate('/help');
                      setMobileMenuOpen(false);
                    }}
                  >
                    Help Center
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="justify-start h-8 px-2 text-xs"
                    onClick={() => {
                      navigate('/about');
                      setMobileMenuOpen(false);
                    }}
                  >
                    About
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="justify-start h-8 px-2 text-xs"
                    onClick={() => {
                      window.open('/api-docs', '_blank');
                      setMobileMenuOpen(false);
                    }}
                  >
                    API Docs
                  </Button>
                </div>
              </div>
              
              {/* Version and Copyright Info */}
              <div className="px-2 py-3 border-t bg-gray-50 rounded-b-xl">
                <p className="text-xs text-gray-500 text-center">
                  © {new Date().getFullYear()} Vaiu Global Inc.
                  <br />
                  <span className="text-gray-400">
                    <VersionInfo format="inline" />
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppShell;
